import React, { useState, useEffect, useCallback } from 'react';
import LegislatorVotesOnTrackedBillsReport from './LegislatorVotesOnTrackedBillsReport';
import { useWorkspaceReports } from '../../hooks/useWorkspaceReports';
import { VotesReportFilters } from '../../types/reports';

interface LegislatorVotesReportWrapperProps {
  workspaceId?: string;
  workspaceName?: string;
  organizationId?: string;
  organizationName?: string;
  filters?: VotesReportFilters;
}

const EMPTY_FILTERS: VotesReportFilters = {};

const LegislatorVotesReportWrapper: React.FC<LegislatorVotesReportWrapperProps> = ({ 
  workspaceId,
  workspaceName = 'Workspace',
  organizationId,
  organizationName,
  filters
}) => {
  const isOrgMode = !!organizationId;
  const displayName = isOrgMode ? (organizationName || 'Organization') : workspaceName;
  const stableFilters = filters || EMPTY_FILTERS;
  
  const [reportData, setReportData] = useState<any>(null);
  const [activeFilters, setActiveFilters] = useState<VotesReportFilters>(stableFilters);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { fetchLegislatorVotesOnTrackedBillsReport, exportReport } = useWorkspaceReports(workspaceId, organizationId);

  useEffect(() => {
    setActiveFilters(stableFilters);
  }, [stableFilters]);

  const loadReportData = useCallback(async (filtersToApply: VotesReportFilters) => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await fetchLegislatorVotesOnTrackedBillsReport(filtersToApply);
      setReportData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load legislator votes report');
    } finally {
      setIsLoading(false);
    }
  }, [fetchLegislatorVotesOnTrackedBillsReport]);

  useEffect(() => {
    if (isOrgMode ? !!organizationId : !!workspaceId) {
      loadReportData(activeFilters);
    }
  }, [workspaceId, organizationId, activeFilters, isOrgMode, loadReportData]);

  const handleFiltersChange = (newFilters: Record<string, unknown>) => {
    setActiveFilters((prev) => ({ ...prev, ...newFilters } as VotesReportFilters));
  };

  const handleExport = async (format: 'excel' | 'csv' | 'pdf') => {
    try {
      await exportReport('legislator-votes-on-tracked-bills', format, { ...activeFilters, viewMode: 'table' });
    } catch (err: any) {
      console.error('Export failed:', err);
    }
  };

  return (
    <LegislatorVotesOnTrackedBillsReport
      workspaceId={workspaceId || ''}
      data={reportData}
      workspaceName={displayName}
      loading={isLoading}
      error={error}
      onFiltersChange={handleFiltersChange}
      onExport={handleExport}
      isOrgMode={isOrgMode}
    />
  );
};

export default LegislatorVotesReportWrapper;
